import { IntlFormatters } from 'react-intl'
import * as Yup from 'yup'
import { IBAN } from 'ibankit'
import { INationality } from 'consts/countries'

export const personalDetailsConfirmationSchemaStepOne = (
  formatMessage: IntlFormatters['formatMessage']
) =>
  Yup.object().shape(
    {
      firstName: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      lastName: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      mobile: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      email: Yup.string()
        .email(
          formatMessage({
            id: 'validation.email',
            defaultMessage: 'Niepoprawny adres email'
          })
        )
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        ),
      nationality: Yup.mixed(),
      hasNoPesel: Yup.boolean(),
      pesel: Yup.string().when(
        ['hasNoPesel', 'nationality'],
        ([hasNoPesel, nationality], schema) => {
          const nationalityCode = (nationality as INationality)?.alpha2?.toUpperCase()
          const isPolish = !nationalityCode || nationalityCode === 'PL'
          const peselFormatMessage = formatMessage({
            id: 'validation.pesel',
            defaultMessage: 'PESEL powinien składać się z 11 cyfr'
          })

          if (!isPolish && hasNoPesel) {
            return schema.notRequired()
          }

          if (!isPolish) {
            return schema.matches(/^\d{11}(:\d{11})*$/, {
              message: peselFormatMessage,
              excludeEmptyString: true
            })
          }

          return schema
            .matches(/^\d{11}(:\d{11})*$/, peselFormatMessage)
            .required(
              formatMessage({
                id: 'validation.required',
                defaultMessage: 'Wymagane'
              })
            )
        }
      ),
      idCard: Yup.string().when(
        ['passportId', 'residenceCardNumber'],
        ([passportId, residenceCardNumber], schema) => {
          if (!(passportId?.length || residenceCardNumber?.length)) {
            return Yup.string().required(
              formatMessage({
                id: 'validation.requiredAtLeastOneDoc',
                defaultMessage: 'Wymagany przynajmniej jeden dokument'
              })
            )
          } else {
            return Yup.string().notRequired()
          }
        }
      ),
      passportId: Yup.string().when(
        ['idCard', 'residenceCardNumber'],
        ([idCard, residenceCardNumber], schema) => {
          if (!(idCard?.length || residenceCardNumber?.length)) {
            return Yup.string().required(
              formatMessage({
                id: 'validation.requiredAtLeastOneDoc',
                defaultMessage: 'Wymagany przynajmniej jeden dokument'
              })
            )
          } else {
            return Yup.string().notRequired()
          }
        }
      ),
      residenceCardNumber: Yup.string().when(
        ['idCard', 'passportId'],
        ([idCard, passportId], schema) => {
          if (idCard?.length || passportId?.length) {
            return Yup.string().notRequired()
          } else {
            return Yup.string().required(
              formatMessage({
                id: 'validation.requiredAtLeastOneDoc',
                defaultMessage: 'Wymagany przynajmniej jeden dokument'
              })
            )
          }
        }
      ),
      dateOfBirth: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      placeOfBirth: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .nullable()
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        ),
      formOfEmployment: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .nullable()
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    },
    [
      ['passportId', 'idCard'],
      ['idCard', 'passportId'],
      ['passportId', 'residenceCardNumber'],
      ['residenceCardNumber', 'passportId'],
      ['idCard', 'residenceCardNumber'],
      ['residenceCardNumber', 'idCard']
    ]
  )

export const personalDetailsConfirmationSchemaStepTwo = (
  formatMessage: IntlFormatters['formatMessage']
) =>
  Yup.object().shape({
    accountNumber: Yup.string()
      .required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      )
      .test('is-valid-iban', function (value) {
        const { path, createError } = this

        // Skip validation if empty (required validator will catch it)
        if (!value) return true

        // Remove spaces for validation
        const cleanValue = value.replace(/\s/g, '')

        // Check if it looks like an IBAN (starts with 2 letters)
        const ibanPattern = /^[A-Z]{2}/i
        if (!ibanPattern.test(cleanValue)) {
          // If it doesn't look like IBAN, it might be a foreign account number
          // Let it pass if it's at least 8 characters (basic check)
          return cleanValue.length >= 8 || createError({
            path,
            message: formatMessage({
              id: 'validation.accountNumber.invalid',
              defaultMessage: 'Nieprawidłowy numer konta'
            })
          })
        }

        // If it looks like IBAN, validate it properly
        try {
          const isValid = IBAN.isValid(cleanValue.toUpperCase())
          if (!isValid) {
            return createError({
              path,
              message: formatMessage({
                id: 'validation.iban.invalid',
                defaultMessage: 'Nieprawidłowy numer IBAN'
              })
            })
          }
          return true
        } catch (error) {
          return createError({
            path,
            message: formatMessage({
              id: 'validation.iban.invalid',
              defaultMessage: 'Nieprawidłowy numer IBAN'
            })
          })
        }
      }),
    bankName: Yup.string().required(
      formatMessage({
        id: 'validation.required',
        defaultMessage: 'Wymagane'
      })
    ),
    billingAddress: Yup.object().shape({
      street: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      city: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      zipcode: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      country: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    }),
    address: Yup.object().shape({
      street: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      city: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      zipcode: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      country: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    }),
    deliveryAddressDuringContract: Yup.object().shape({
      street: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      city: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      zipcode: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      country: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    }),
    deliveryAddressAfterContract: Yup.object().shape({
      street: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      city: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      zipcode: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      ),
      country: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string()
        })
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        )
    })
  })
